/**
 * SQLite-based connector schema cache
 */

import Database from "better-sqlite3";
import { join } from "node:path";
import { existsSync, mkdirSync, chmodSync } from "node:fs";
import { runMigrations } from "./migrations.js";
import { getConfigDir } from "../config/loader.js";
import { createChildLogger } from "../utils/logger.js";
import { SCHEMA_CACHE_FILE_NAME } from "../utils/constants.js";
import type { Connector, SwaggerOperation } from "../api/types.js";
import { escapeFts5Query, sanitizeSearchQuery } from "../utils/security.js";

const cacheLogger = createChildLogger({ module: "schema-cache" });

/**
 * Cached connector record
 */
export interface CachedConnector {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  iconUri: string | null;
  publisher: string | null;
  tier: string;
  isCustomApi: boolean;
  capabilities: string[] | null;
  updatedAt: string;
}

/**
 * Cached operation record
 */
export interface CachedOperation {
  connectorId: string;
  operationId: string;
  type: string;
  method: string | null;
  path: string | null;
  summary: string | null;
  description: string | null;
  visibility: string | null;
  isTrigger: boolean;
  schema: SwaggerOperation | null;
}

/**
 * Search result
 */
export interface ConnectorSearchResult {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  publisher: string | null;
  tier: string;
  rank: number;
}

/**
 * Schema cache manager
 */
export class SchemaCache {
  private db: Database.Database;
  private readonly dbPath: string;

  constructor(customPath?: string) {
    this.dbPath = customPath ?? join(getConfigDir(), SCHEMA_CACHE_FILE_NAME);

    // Ensure directory exists
    const dir = join(this.dbPath, "..");
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true });
    }

    cacheLogger.debug({ dbPath: this.dbPath }, "Opening schema cache database");

    this.db = new Database(this.dbPath);
    this.db.pragma("journal_mode = WAL");
    this.db.pragma("foreign_keys = ON");

    // Secure file permissions (owner read/write only) — non-Windows
    if (process.platform !== "win32") {
      try {
        chmodSync(this.dbPath, 0o600);
      } catch {
        // Best effort — may fail if file is owned by another user
      }
    }

    // Run migrations
    runMigrations(this.db);

    cacheLogger.info({ dbPath: this.dbPath }, "Schema cache initialized");
  }

  /**
   * Close the database connection
   */
  close(): void {
    this.db.close();
    cacheLogger.debug("Schema cache closed");
  }

  /**
   * Get cache last update time
   */
  getLastUpdateTime(): Date | null {
    const result = this.db.prepare(
      "SELECT value FROM cache_metadata WHERE key = 'last_full_sync'"
    ).get() as { value: string } | undefined;

    return result ? new Date(result.value) : null;
  }

  /**
   * Set cache last update time
   */
  setLastUpdateTime(time: Date = new Date()): void {
    this.db.prepare(`
      INSERT OR REPLACE INTO cache_metadata (key, value, updated_at)
      VALUES ('last_full_sync', ?, datetime('now'))
    `).run(time.toISOString());
  }

  /**
   * Check if cache needs refresh
   */
  needsRefresh(maxAgeHours: number): boolean {
    const lastUpdate = this.getLastUpdateTime();
    if (!lastUpdate) return true;

    const ageMs = Date.now() - lastUpdate.getTime();
    const maxAgeMs = maxAgeHours * 60 * 60 * 1000;
    return ageMs > maxAgeMs;
  }

  /**
   * Upsert a connector into the cache
   */
  upsertConnector(connector: Connector): void {
    const props = connector.properties;

    this.db.prepare(`
      INSERT INTO connectors (id, name, display_name, description, icon_uri, publisher, tier, is_custom_api, capabilities, updated_at)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'))
      ON CONFLICT(id) DO UPDATE SET
        name = excluded.name,
        display_name = excluded.display_name,
        description = excluded.description,
        icon_uri = excluded.icon_uri,
        publisher = excluded.publisher,
        tier = excluded.tier,
        is_custom_api = excluded.is_custom_api,
        capabilities = excluded.capabilities,
        updated_at = datetime('now')
    `).run(
      connector.id,
      connector.name,
      props.displayName,
      props.description ?? null,
      props.iconUri ?? null,
      props.publisher ?? null,
      props.tier,
      props.isCustomApi ? 1 : 0,
      props.capabilities ? JSON.stringify(props.capabilities) : null
    );
  }

  /**
   * Upsert an operation into the cache
   */
  upsertOperation(
    connectorId: string,
    operationId: string,
    method: string,
    path: string,
    operation: SwaggerOperation
  ): void {
    const isTrigger = operation["x-ms-trigger"] !== undefined;

    this.db.prepare(`
      INSERT INTO operations (connector_id, operation_id, type, method, path, summary, description, visibility, is_trigger, schema_json, updated_at)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'))
      ON CONFLICT(connector_id, operation_id) DO UPDATE SET
        type = excluded.type,
        method = excluded.method,
        path = excluded.path,
        summary = excluded.summary,
        description = excluded.description,
        visibility = excluded.visibility,
        is_trigger = excluded.is_trigger,
        schema_json = excluded.schema_json,
        updated_at = datetime('now')
    `).run(
      connectorId,
      operationId,
      isTrigger ? "trigger" : "action",
      method.toUpperCase(),
      path,
      operation.summary ?? null,
      operation.description ?? null,
      operation["x-ms-visibility"] ?? null,
      isTrigger ? 1 : 0,
      JSON.stringify(operation)
    );
  }

  /**
   * Bulk upsert connectors (uses transaction)
   */
  upsertConnectorsBulk(connectors: Connector[]): void {
    const upsert = this.db.transaction((items: Connector[]) => {
      for (const connector of items) {
        this.upsertConnector(connector);
      }
    });

    upsert(connectors);
    cacheLogger.info({ count: connectors.length }, "Bulk upserted connectors");
  }

  /**
   * Get connector by ID
   */
  getConnector(connectorId: string): CachedConnector | null {
    const row = this.db.prepare(`
      SELECT id, name, display_name, description, icon_uri, publisher, tier, is_custom_api, capabilities, updated_at
      FROM connectors WHERE id = ?
    `).get(connectorId) as {
      id: string;
      name: string;
      display_name: string;
      description: string | null;
      icon_uri: string | null;
      publisher: string | null;
      tier: string;
      is_custom_api: number;
      capabilities: string | null;
      updated_at: string;
    } | undefined;

    if (!row) return null;

    return {
      id: row.id,
      name: row.name,
      displayName: row.display_name,
      description: row.description,
      iconUri: row.icon_uri,
      publisher: row.publisher,
      tier: row.tier,
      isCustomApi: row.is_custom_api === 1,
      capabilities: row.capabilities ? (JSON.parse(row.capabilities) as string[]) : null,
      updatedAt: row.updated_at,
    };
  }

  /**
   * Search connectors by text
   */
  searchConnectors(query: string, limit = 20): ConnectorSearchResult[] {
    // Sanitize and escape the query to prevent FTS5 injection
    const sanitized = sanitizeSearchQuery(query);
    if (!sanitized) {
      return []; // Empty query after sanitization
    }
    const safeQuery = escapeFts5Query(sanitized);

    // Use FTS5 for full-text search
    const rows = this.db.prepare(`
      SELECT c.id, c.name, c.display_name, c.description, c.publisher, c.tier,
             bm25(connectors_fts) as rank
      FROM connectors_fts
      JOIN connectors c ON connectors_fts.rowid = c.rowid
      WHERE connectors_fts MATCH ?
      ORDER BY rank
      LIMIT ?
    `).all(safeQuery, limit) as Array<{
      id: string;
      name: string;
      display_name: string;
      description: string | null;
      publisher: string | null;
      tier: string;
      rank: number;
    }>;

    return rows.map((row) => ({
      id: row.id,
      name: row.name,
      displayName: row.display_name,
      description: row.description,
      publisher: row.publisher,
      tier: row.tier,
      rank: row.rank,
    }));
  }

  /**
   * List connectors with optional filtering
   */
  listConnectors(options: {
    tier?: string;
    limit?: number;
    offset?: number;
  } = {}): CachedConnector[] {
    const { tier, limit = 50, offset = 0 } = options;

    let sql = `
      SELECT id, name, display_name, description, icon_uri, publisher, tier, is_custom_api, capabilities, updated_at
      FROM connectors
    `;
    const params: (string | number)[] = [];

    if (tier) {
      sql += " WHERE tier = ?";
      params.push(tier);
    }

    sql += " ORDER BY display_name LIMIT ? OFFSET ?";
    params.push(limit, offset);

    const rows = this.db.prepare(sql).all(...params) as Array<{
      id: string;
      name: string;
      display_name: string;
      description: string | null;
      icon_uri: string | null;
      publisher: string | null;
      tier: string;
      is_custom_api: number;
      capabilities: string | null;
      updated_at: string;
    }>;

    return rows.map((row) => ({
      id: row.id,
      name: row.name,
      displayName: row.display_name,
      description: row.description,
      iconUri: row.icon_uri,
      publisher: row.publisher,
      tier: row.tier,
      isCustomApi: row.is_custom_api === 1,
      capabilities: row.capabilities ? (JSON.parse(row.capabilities) as string[]) : null,
      updatedAt: row.updated_at,
    }));
  }

  /**
   * Get operations for a connector
   */
  getOperations(connectorId: string): CachedOperation[] {
    const rows = this.db.prepare(`
      SELECT connector_id, operation_id, type, method, path, summary, description, visibility, is_trigger, schema_json
      FROM operations
      WHERE connector_id = ?
      ORDER BY operation_id
    `).all(connectorId) as Array<{
      connector_id: string;
      operation_id: string;
      type: string;
      method: string | null;
      path: string | null;
      summary: string | null;
      description: string | null;
      visibility: string | null;
      is_trigger: number;
      schema_json: string | null;
    }>;

    return rows.map((row) => ({
      connectorId: row.connector_id,
      operationId: row.operation_id,
      type: row.type,
      method: row.method,
      path: row.path,
      summary: row.summary,
      description: row.description,
      visibility: row.visibility,
      isTrigger: row.is_trigger === 1,
      schema: row.schema_json ? (JSON.parse(row.schema_json) as SwaggerOperation) : null,
    }));
  }

  /**
   * Get a specific operation
   */
  getOperation(connectorId: string, operationId: string): CachedOperation | null {
    const row = this.db.prepare(`
      SELECT connector_id, operation_id, type, method, path, summary, description, visibility, is_trigger, schema_json
      FROM operations
      WHERE connector_id = ? AND operation_id = ?
    `).get(connectorId, operationId) as {
      connector_id: string;
      operation_id: string;
      type: string;
      method: string | null;
      path: string | null;
      summary: string | null;
      description: string | null;
      visibility: string | null;
      is_trigger: number;
      schema_json: string | null;
    } | undefined;

    if (!row) return null;

    return {
      connectorId: row.connector_id,
      operationId: row.operation_id,
      type: row.type,
      method: row.method,
      path: row.path,
      summary: row.summary,
      description: row.description,
      visibility: row.visibility,
      isTrigger: row.is_trigger === 1,
      schema: row.schema_json ? (JSON.parse(row.schema_json) as SwaggerOperation) : null,
    };
  }

  /**
   * Get connector count
   */
  getConnectorCount(): number {
    const result = this.db.prepare("SELECT COUNT(*) as count FROM connectors").get() as { count: number };
    return result.count;
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.db.exec("DELETE FROM operations");
    this.db.exec("DELETE FROM connectors");
    this.db.exec("DELETE FROM cache_metadata WHERE key = 'last_full_sync'");
    cacheLogger.info("Schema cache cleared");
  }
}
